import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { accionDeEvento, buscarPorHuella, modoEfectivo, normalizarModo, elementoMarcable, elementosProtegidos, esModoMarcador, huellaDe, huellaSoloEste, protegidoDe, sugerenciaDe } from './huella-rbac';

/**
 * MODO MARCADOR: la consola dentro del iframe de la pestaña «Agregar permisos».
 *
 * La pestaña abre `<iframe name="rbac-marcador" src="<esta misma consola>/<ruta>">`. Aquí la pantalla se ve con datos
 * reales pero NO se puede usar: ningún clic, envío ni Enter/Espacio llega a la pantalla (se frena en la captura,
 * antes de que lo vea Angular), y el interceptor (rbac-marcador.interceptor.ts) rechaza todo lo que no sea GET.
 * Un clic solo SELECCIONA: se calcula la huella del elemento y se la manda a la pestaña.
 *
 * Mensajes (siempre con location.origin; quien recibe valida el origen):
 *   iframe -> pestaña: rbac-marcador:listo { ruta }, rbac-marcador:toque { huella, coincidencias, sugerencia }, rbac-marcador:salir (tecla Esc dentro del iframe),
 *                      rbac-marcador:llamadas { llamadas:[{ metodo, url }] } (las peticiones de la pantalla, sin dominio ni query)
 *   pestaña -> iframe: rbac-marcador:marcas { marcas:[{ huella, etiqueta }] }, :subir, :limpiar,
 *                      rbac-marcador:modo { modo:'marcar'|'usar' } («usar»: los clics pasan a la pantalla para abrir menús, modales y pestañas; el interceptor sigue frenando todo lo que no sea GET)
 *
 * Fuera de este modo (pestaña normal) iniciar() no hace nada. Todo corre fuera de la zona de Angular: mover el mouse
 * no debe disparar la detección de cambios de la consola.
 */

const EVENTOS_BLOQUEADOS = ['click', 'dblclick', 'auxclick', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'submit'];
const EVENTOS_DE_TECLA = ['keydown', 'keyup', 'keypress'];
const CLASE_MODO_MARCADOR = 'rbac-modo-marcador';
const ID_CAPA = 'rbac-marcador-capa';
const MAX_ETIQUETAS = 60;
const MAX_YA_PROTEGIDOS = 150;
const MAX_LLAMADAS = 200;
const ESPERA_LLAMADAS_MS = 300;

/** Las peticiones que hizo la pantalla (método + URL sin dominio ni query), sin repetir. Las anota el interceptor. */
const llamadasRegistradas: { metodo: string; url: string }[] = [];
let avisarLlamadas: (() => void) | null = null;

/** Anota una petición de la pantalla (solo en modo marcador; el interceptor ya lo comprobó). */
export const registrarLlamadaMarcador = (metodo: any, url: any): void => {
  const m = String(metodo || '').toUpperCase();
  const u = '/' + String(url === null || url === undefined ? '' : url)
    .replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/]*/, '')
    .split(/[?#]/)[0]
    .split('/')
    .filter((s: string) => s !== '')
    .join('/');
  if (!m || u === '/') return;
  if (llamadasRegistradas.length >= MAX_LLAMADAS || llamadasRegistradas.some((l) => l.metodo === m && l.url === u)) return;
  llamadasRegistradas.push({ metodo: m, url: u });
  if (avisarLlamadas) avisarLlamadas();
};

@Injectable({ providedIn: 'root' })
export class RbacMarcadorService {

  private activo = false;
  private capa: HTMLElement | null = null;
  private cajaHover: HTMLElement | null = null;
  private cajaSeleccion: HTMLElement | null = null;
  private nodosMarcas: HTMLElement[] = [];

  private hover: any = null;
  private seleccion: any = null;
  private marcas: any[] = [];
  private encontrados: { el: any, etiqueta: string }[] = [];
  /** Lo que YA tiene permiso: marcas guardadas y elementos con data-rbac-permiso (el código de la plantilla). */
  private yaProtegidos: { el: any, permisos: string[] }[] = [];
  private nombres: any = {};
  private cajaTip: HTMLElement | null = null;
  private buscarPendiente = false;
  private dibujoPendiente = false;
  private envioLlamadas: any = null;
  /** 'marcar' | 'usar' (lo manda la pestaña) y Alt presionado = «usar» temporal. */
  private modo = 'marcar';
  private alt = false;

  constructor(private router: Router, private zone: NgZone) { }

  iniciar(): void {
    if (this.activo || !esModoMarcador(window)) return;
    this.activo = true;
    // La pantalla se ve sola, a todo el ancho: styles.css esconde el menú y la barra de la consola con esta clase.
    document.documentElement.classList.add(CLASE_MODO_MARCADOR);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('keydown', this.alPulsarEsc, true);
      window.addEventListener('keydown', this.alCambiarAlt, true);
      window.addEventListener('keyup', this.alCambiarAlt, true);
      window.addEventListener('blur', this.alPerderFoco);
      this.montarCapa();

      // Captura en window: llega antes que cualquier otro oyente de la página.
      for (const tipo of EVENTOS_BLOQUEADOS) window.addEventListener(tipo, this.frenar, true);
      for (const tipo of EVENTOS_DE_TECLA) window.addEventListener(tipo, this.frenar, true);
      window.addEventListener('mouseover', this.alPasarElMouse, true);
      window.addEventListener('mouseout', this.alSalirElMouse, true);
      window.addEventListener('message', this.alRecibirMensaje);
      window.addEventListener('scroll', this.pedirDibujo, { capture: true, passive: true });
      window.addEventListener('resize', this.pedirDibujo, { passive: true });

      // La pantalla cambia sola (datos que llegan, *ngIf): se redibujan las etiquetas, sin tocar el diseño.
      new MutationObserver(() => this.pedirDibujo(true)).observe(document.body, { childList: true, subtree: true });
    });

    this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd)).subscribe(() => {
      this.quitarSeleccion();
      this.avisarListo();
    });
    this.avisarListo();

    // Las peticiones de la pantalla llegan a la pestaña juntas (cada 300 ms como mucho), con lo que ya se haya pedido.
    avisarLlamadas = () => this.pedirEnvioLlamadas();
    this.pedirEnvioLlamadas();
  }

  private pedirEnvioLlamadas(): void {
    if (this.envioLlamadas || !llamadasRegistradas.length) return;
    this.envioLlamadas = setTimeout(() => {
      this.envioLlamadas = null;
      this.enviar({ tipo: 'rbac-marcador:llamadas', llamadas: llamadasRegistradas.map((l) => ({ metodo: l.metodo, url: l.url })) });
    }, ESPERA_LLAMADAS_MS);
  }

  // ───────────────────────── Frenar la pantalla ─────────────────────────

  /** Esc dentro del iframe: la pestaña no lo recibe, así que se le avisa para que salga de pantalla completa. */
  private alPulsarEsc = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && window.parent && window.parent !== window) {
      window.parent.postMessage({ tipo: 'rbac-marcador:salir' }, location.origin);
    }
  };

  /** Alt presionado = «usar» temporal (no frena nada: solo se anota). */
  private alCambiarAlt = (event: KeyboardEvent): void => {
    if (event.key !== 'Alt') return;
    this.alt = event.type === 'keydown';
    if (this.alt) this.limpiarHover();
  };

  private alPerderFoco = (): void => { this.alt = false; };

  private modoActual(): string { return modoEfectivo(this.modo, this.alt); }

  private limpiarHover(): void {
    this.hover = null;
    this.pintarCajas();
    if (this.cajaTip) this.cajaTip.style.display = 'none';
  }

  private frenar = (event: Event): void => {
    const accion = accionDeEvento(this.modoActual(), event.type);
    if (accion === 'pasar') return;
    if (event.type.indexOf('key') === 0) {
      const tecla = (event as KeyboardEvent).key;
      if (tecla !== 'Enter' && tecla !== ' ' && tecla !== 'Spacebar') return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (accion === 'marcar') this.alTocar(event.target);
  }

  private alTocar(destino: any): void {
    if (!destino || destino.nodeType === undefined) return;
    const el = elementoMarcable(destino);
    const t = el && (el.localName || '').toLowerCase();
    if (!el || t === 'body' || t === 'html') return;
    this.seleccionar(el);
  }

  private seleccionar(el: any): void {
    this.seleccion = el;
    this.pintarCajas();
    const huella = huellaDe(el);
    const coincidencias = buscarPorHuella(document, huella).length;
    this.enviar({
      tipo: 'rbac-marcador:toque',
      huella,
      coincidencias,
      huellaSolo: coincidencias > 1 ? huellaSoloEste(document, el) : null,
      sugerencia: sugerenciaDe(el),
      yaTiene: this.yaTieneDe(el),
    });
  }

  /** Permisos que el elemento tocado ya tiene (él o un ancestro): del código (data-rbac-permiso) o de una marca guardada. */
  private yaTieneDe(el: any): { codigo: string, texto: string }[] {
    const codigos: string[] = [];
    const p = protegidoDe(el);
    if (p) for (const c of p.permisos) if (codigos.indexOf(c) < 0) codigos.push(c);
    this.buscarYaProtegidos();
    for (const y of this.yaProtegidos) {
      if (y.el.contains(el)) for (const c of y.permisos) if (codigos.indexOf(c) < 0) codigos.push(c);
    }
    return codigos.map((c) => ({ codigo: c, texto: this.nombres[c] || c }));
  }

  private subirSeleccion(): void {
    if (!this.seleccion) return;
    const padre = this.seleccion.parentElement;
    const t = padre && (padre.localName || '').toLowerCase();
    if (padre && t !== 'body' && t !== 'html') this.seleccion = padre;
    this.seleccionar(this.seleccion); // en el tope se repite la misma selección
  }

  private quitarSeleccion(): void {
    this.seleccion = null;
    this.hover = null;
    this.pintarCajas();
  }

  // ───────────────────────── Mensajes ─────────────────────────

  private enviar(mensaje: any): void {
    try {
      window.parent.postMessage(mensaje, location.origin);
    } catch (e) {
      // La pestaña ya no está: no hay a quién avisar.
    }
  }

  private avisarListo(): void {
    this.enviar({ tipo: 'rbac-marcador:listo', ruta: location.pathname });
  }

  private alRecibirMensaje = (event: MessageEvent): void => {
    if (event.origin !== location.origin) return;
    if (event.source !== window.parent) return;
    const msg: any = event.data;
    if (!msg || typeof msg.tipo !== 'string') return;
    switch (msg.tipo) {
      case 'rbac-marcador:subir':
        this.subirSeleccion();
        break;
      case 'rbac-marcador:modo':
        this.modo = normalizarModo(msg.modo);
        if (this.modoActual() === 'usar') this.limpiarHover(); // lo abierto (menú, modal, pestaña) queda tal cual
        break;
      case 'rbac-marcador:limpiar':
        this.quitarSeleccion();
        break;
      case 'rbac-marcador:marcas':
        this.marcas = Array.isArray(msg.marcas)
          ? msg.marcas.filter((m: any) => m && m.huella && typeof m.huella.selector === 'string')
          : [];
        this.nombres = msg.nombres && typeof msg.nombres === 'object' ? msg.nombres : {};
        this.pedirDibujo(true);
        break;
    }
  }

  // ───────────────────────── Resaltado (capa superpuesta) ─────────────────────────

  private estiloCaja(borde: string, fondo: string, estilo: string): string {
    return 'position:fixed;display:none;box-sizing:border-box;pointer-events:none;border-radius:3px;'
      + 'border:2px ' + estilo + ' ' + borde + ';background:' + fondo + ';';
  }

  /** Una capa fija encima de todo, hija de <html> (no de <body>) para no alterar el diseño ni el observador. */
  private montarCapa(): void {
    const capa = document.createElement('div');
    capa.id = ID_CAPA;
    capa.setAttribute('aria-hidden', 'true');
    capa.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;overflow:visible;pointer-events:none;z-index:2147483647;';
    this.cajaHover = document.createElement('div');
    this.cajaHover.style.cssText = this.estiloCaja('#2563eb', 'rgba(37,99,235,.08)', 'solid');
    this.cajaSeleccion = document.createElement('div');
    this.cajaSeleccion.style.cssText = this.estiloCaja('#16a34a', 'rgba(22,163,74,.12)', 'solid');
    this.cajaTip = document.createElement('div');
    this.cajaTip.style.cssText = 'position:fixed;display:none;max-width:300px;pointer-events:none;'
      + 'font:500 12px/16px system-ui,sans-serif;color:#fff;background:#1f2937;padding:4px 8px;border-radius:4px;'
      + 'box-shadow:0 2px 8px rgba(0,0,0,.3);';
    capa.appendChild(this.cajaTip);
    capa.appendChild(this.cajaHover);
    capa.appendChild(this.cajaSeleccion);
    document.documentElement.appendChild(capa);
    this.capa = capa;
  }

  private alPasarElMouse = (event: Event): void => {
    if (this.modoActual() === 'usar') return;
    const destino: any = event.target;
    if (!destino || destino.nodeType !== 1) return;
    const el = elementoMarcable(destino);
    const t = (el.localName || '').toLowerCase();
    this.hover = t === 'body' || t === 'html' ? null : el;
    this.pintarCajas();
    this.mostrarTip(destino, (event as MouseEvent).clientX, (event as MouseEvent).clientY);
  }

  /** Tooltip «Ya tiene permiso: …» al pasar el mouse sobre algo que ya está protegido (el más interno gana). */
  private mostrarTip(destino: any, x: number, y: number): void {
    if (!this.cajaTip) return;
    let mejor: { el: any, permisos: string[] } | null = null;
    for (const p of this.yaProtegidos) {
      if (p.el.isConnected && p.el.contains(destino) && (!mejor || mejor.el.contains(p.el))) mejor = p;
    }
    if (!mejor) { this.cajaTip.style.display = 'none'; return; }
    this.cajaTip.textContent = 'Ya tiene permiso: ' + mejor.permisos.map((c) => this.nombres[c] || c).join(' · ');
    this.cajaTip.style.display = 'block';
    this.cajaTip.style.left = Math.min(x + 12, Math.max(0, window.innerWidth - 320)) + 'px';
    this.cajaTip.style.top = Math.min(y + 16, Math.max(0, window.innerHeight - 40)) + 'px';
  }

  private alSalirElMouse = (event: Event): void => {
    if ((event as MouseEvent).relatedTarget) return; // sigue dentro de la página
    this.hover = null;
    this.pintarCajas();
    if (this.cajaTip) this.cajaTip.style.display = 'none';
  }

  private pedirDibujo = (buscar: any = false): void => {
    // Los oyentes de scroll/resize pasan un Event: solo `true` vuelve a buscar las coincidencias.
    if (buscar === true) this.buscarPendiente = true;
    if (this.dibujoPendiente) return;
    this.dibujoPendiente = true;
    requestAnimationFrame(() => {
      this.dibujoPendiente = false;
      this.pintar();
    });
  }

  private ubicar(caja: HTMLElement | null, el: any): void {
    if (!caja) return;
    if (!el || !el.isConnected) { caja.style.display = 'none'; return; }
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) { caja.style.display = 'none'; return; }
    caja.style.display = 'block';
    caja.style.left = r.left + 'px';
    caja.style.top = r.top + 'px';
    caja.style.width = r.width + 'px';
    caja.style.height = r.height + 'px';
  }

  private buscarMarcadas(): void {
    this.encontrados = [];
    for (const m of this.marcas) {
      if (m.ya) continue; // lo ya guardado se dibuja atenuado (buscarYaProtegidos), no como chip
      for (const el of buscarPorHuella(document, m.huella)) {
        this.encontrados.push({ el, etiqueta: String(m.etiqueta || m.huella.etiqueta || '') });
        if (this.encontrados.length >= MAX_ETIQUETAS) break;
      }
    }
    this.buscarYaProtegidos();
  }

  /** Lo que ya tiene permiso: (1) marcas guardadas de esta ruta y (2) lo que el código protege (data-rbac-permiso). */
  private buscarYaProtegidos(): void {
    const lista: { el: any, permisos: string[] }[] = [];
    const poner = (el: any, permisos: string[]) => {
      const previo = lista.find((x) => x.el === el);
      if (previo) { for (const c of permisos) if (previo.permisos.indexOf(c) < 0) previo.permisos.push(c); return; }
      if (lista.length < MAX_YA_PROTEGIDOS) lista.push({ el, permisos: permisos.slice() });
    };
    for (const m of this.marcas) {
      if (!m.ya || !m.permiso) continue;
      for (const el of buscarPorHuella(document, m.huella)) poner(el, [m.permiso]);
    }
    for (const p of elementosProtegidos(document)) poner(p.el, p.permisos);
    this.yaProtegidos = lista;
  }

  /** El contorno del hover y el de la selección: es lo único que se mueve con el mouse. */
  private pintarCajas(): void {
    if (!this.capa) return;
    this.ubicar(this.cajaHover, this.hover === this.seleccion ? null : this.hover);
    this.ubicar(this.cajaSeleccion, this.seleccion);
  }

  /** Redibuja el hover, la selección y una etiqueta pequeña sobre cada elemento ya marcado. */
  private pintar(): void {
    if (!this.capa) return;
    this.pintarCajas();

    for (const n of this.nodosMarcas) n.remove();
    this.nodosMarcas = [];
    if (this.buscarPendiente) {
      this.buscarPendiente = false;
      this.buscarMarcadas();
    }

    // Lo que ya tiene permiso: atenuado, con borde sutil (el tooltip sale al pasar el mouse). Sin chip: no sobrecarga.
    for (const { el } of this.yaProtegidos) {
      if (!el.isConnected) continue;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) continue;
      if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) continue;
      const caja = document.createElement('div');
      caja.style.cssText = this.estiloCaja('rgba(100,116,139,.55)', 'rgba(255,255,255,.55)', 'dotted')
        + 'border-width:1px;display:block;left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width + 'px;height:' + r.height + 'px;';
      this.capa.appendChild(caja);
      this.nodosMarcas.push(caja);
    }

    for (const { el, etiqueta } of this.encontrados) {
      if (!el.isConnected) continue;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) continue;
      if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) continue;
      const caja = document.createElement('div');
      caja.style.cssText = this.estiloCaja('#7c3aed', 'rgba(124,58,237,.08)', 'dashed')
        + 'display:block;left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width + 'px;height:' + r.height + 'px;';
      const chip = document.createElement('span');
      chip.textContent = etiqueta;
      chip.style.cssText = 'position:absolute;left:-2px;' + (r.top < 20 ? 'top:0;' : 'bottom:100%;')
        + 'max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'
        + 'font:600 11px/16px system-ui,sans-serif;color:#fff;background:#7c3aed;padding:0 6px;border-radius:3px;';
      caja.appendChild(chip);
      this.capa.appendChild(caja);
      this.nodosMarcas.push(caja);
    }
  }
}
